import React, { useState } from "react";
import { ethers } from "ethers";
import type { VaultInfo } from "../hooks/useVaults";
import { VAULT_ABI, ERC20_ABI, COLORS } from "../constants";

interface Props {
  vault: VaultInfo;
  signer: ethers.Signer | null;
  onRefresh: () => void;
}

type Tab = "deposit" | "withdraw";

// ── LP Token Tooltip ──────────────────────────────────────────────────────────
const LpTooltip: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          color: COLORS.textMuted, fontSize: 11, padding: "0 4px",
          lineHeight: 1, verticalAlign: "middle",
        }}
        title="What is an LP token?"
      >
        ?
      </button>
      {open && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: "50%",
          transform: "translateX(-50%)", zIndex: 100,
          background: "#1a1a35", border: `1px solid ${COLORS.border}`,
          borderRadius: 10, padding: "12px 14px", width: 240,
          fontSize: 12, color: COLORS.textSub, lineHeight: 1.6,
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        }}>
          <div style={{ fontWeight: 700, color: COLORS.textPrimary, marginBottom: 6 }}>
            What is an LP token?
          </div>
          When you deposit two tokens into a liquidity pool on Aerodrome, you receive
          LP tokens as a receipt. They represent your share of that pool.
          <br /><br />
          Deposit your LP tokens here and we'll auto-compound your AERO rewards back
          into more LP — growing your position automatically.
          <button
            onClick={() => setOpen(false)}
            style={{
              display: "block", marginTop: 10, background: "none",
              border: "none", color: COLORS.textMuted, cursor: "pointer",
              fontSize: 11,
            }}
          >
            Got it ✕
          </button>
        </div>
      )}
    </span>
  );
};

// ── Perf Fee Tooltip ─────────────────────────────────────────────────────────────
const PerfFeeTooltip: React.FC<{ perfPct: string; withdrawPct: string }> = ({ perfPct, withdrawPct }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 3 }}>
      <span>{perfPct}%</span>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{
          background: "none", border: "1px solid rgba(100,116,139,0.3)", borderRadius: "50%",
          cursor: "pointer", color: COLORS.textMuted, fontSize: 9, fontWeight: 700,
          width: 14, height: 14, lineHeight: "12px", textAlign: "center" as const,
          padding: 0, flexShrink: 0,
        }}
        title="How do fees work?"
      >?</button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", bottom: "calc(100% + 8px)", left: "50%",
            transform: "translateX(-50%)", zIndex: 200,
            background: "#141428", border: `1px solid ${COLORS.border}`,
            borderRadius: 12, padding: "14px 16px", width: 260,
            fontSize: 12, color: COLORS.textSub, lineHeight: 1.7,
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          }}
        >
          <div style={{ fontWeight: 700, color: COLORS.textPrimary, marginBottom: 8, fontSize: 13 }}>
            How fees work
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: "#34d399", fontWeight: 700 }}>No deposit fees.</span>
            {" "}We never take a cut of what you put in. Your LP is yours.
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ color: COLORS.yellow, fontWeight: 700 }}>{perfPct}% performance fee</span>
            {" "}— only on the rewards we auto-compound for you. If we earn nothing, we charge nothing.
          </div>
          <div style={{ marginBottom: 10 }}>
            <span style={{ color: COLORS.textSub, fontWeight: 600 }}>{withdrawPct}% withdrawal fee</span>
            {" "}— a small fee on the LP you withdraw. Emergency withdrawals waive this.
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: "none", border: "none", color: COLORS.textMuted,
              cursor: "pointer", fontSize: 11, padding: 0,
            }}
          >Got it ✕</button>
        </div>
      )}
    </span>
  );
};

// ── APR Badge ─────────────────────────────────────────────────────────────────
const AprBadge: React.FC<{ apr: number | null; aprCompounded: number | null }> = ({
  apr, aprCompounded,
}) => {
  const [showBase, setShowBase] = useState(false);
  if (apr === null) return (
    <div style={s.aprBadge}>
      <span style={s.aprLabel}>APR</span>
      <span style={{ ...s.aprValue, color: COLORS.textMuted, fontSize: 13 }}>—</span>
    </div>
  );
  const displayed = showBase ? apr : (aprCompounded ?? apr);
  const label     = showBase ? "Base APR" : "Auto APR";
  return (
    <div
      style={{ ...s.aprBadge, cursor: "pointer" }}
      onClick={() => setShowBase(!showBase)}
      title={showBase
        ? `Click to see compounded APR (${aprCompounded?.toFixed(1)}%)`
        : `Click to see base APR (${apr.toFixed(1)}%)`}
    >
      <span style={s.aprLabel}>{label} ↕</span>
      <span style={{
        ...s.aprValue,
        color: displayed > 50 ? COLORS.green : displayed > 20 ? COLORS.yellow : COLORS.accent,
      }}>
        {displayed.toFixed(1)}%
      </span>
    </div>
  );
};

// ── Get LP Button ─────────────────────────────────────────────────────────────
const GetLpButton: React.FC<{ token0: string; token1: string; isStable: boolean }> = ({
  token0, token1, isStable,
}) => {
  const url = `https://aerodrome.finance/deposit?token0=${token0}&token1=${token1}&stable=${isStable}`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      style={s.getLpBtn}
    >
      Get LP on Aerodrome →
    </a>
  );
};

// ── Main VaultCard ─────────────────────────────────────────────────────────────
export const VaultCard: React.FC<Props> = ({ vault, signer, onRefresh }) => {
  const [tab, setTab]         = useState<Tab>("deposit");
  const [amount, setAmount]   = useState("");
  const [status, setStatus]   = useState<{ text: string; ok?: boolean } | null>(null);
  const [busy, setBusy]       = useState(false);
  const [emergency, setEmerg] = useState(false);

  const perfPct     = (vault.perfFeeBps / 100).toFixed(1);
  const withdrawPct = (vault.withdrawFeeBps / 100).toFixed(2);
  const lastHarvest = vault.lastHarvest ? timeSince(vault.lastHarvest) : "Never";
  const hasPosition = parseFloat(vault.userShares) > 0;

  const estimatedLpOut = (() => {
    if (tab !== "withdraw" || !amount) return null;
    const shares = parseFloat(amount);
    const total  = parseFloat(vault.userShares);
    if (!shares || !total) return null;
    const fraction = Math.min(shares / total, 1);
    const gross    = fraction * parseFloat(vault.userLpValue);
    const fee      = gross * vault.withdrawFeeBps / 10_000;
    return { gross: gross.toFixed(6), net: (gross - fee).toFixed(6), fee: fee.toFixed(6) };
  })();

  async function sendAndWait(
    contractAddress: string,
    abi: readonly string[],
    method: string,
    args: unknown[]
  ): Promise<void> {
    const iface    = new ethers.Interface(abi);
    const data     = iface.encodeFunctionData(method, args);
    const from     = await signer!.getAddress();
    const provider = signer!.provider as ethers.BrowserProvider;
    const [gasEstimate, feeData] = await Promise.all([
      provider.estimateGas({ to: contractAddress, from, data }),
      provider.getFeeData(),
    ]);
    const txParams = {
      to: contractAddress, from, data,
      gas: ethers.toQuantity(gasEstimate * 120n / 100n),
      maxFeePerGas: ethers.toQuantity(feeData.maxFeePerGas ?? feeData.gasPrice ?? 0n),
      maxPriorityFeePerGas: ethers.toQuantity(feeData.maxPriorityFeePerGas ?? 0n),
      type: "0x2",
    };
    const hash = await provider.send("eth_sendTransaction", [txParams]) as string;
    setStatus({ text: "Confirming..." });
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 2000));
      const receipt = await provider.send("eth_getTransactionReceipt", [hash]) as { status: string } | null;
      if (receipt) {
        if (receipt.status === "0x0") throw new Error("Transaction reverted on-chain");
        return;
      }
    }
    throw new Error("Transaction not confirmed after 2 minutes");
  }

  async function handleApprove() {
    if (!signer) return;
    setBusy(true); setStatus({ text: "Waiting for approval..." });
    try {
      await sendAndWait(vault.lpTokenAddress, ERC20_ABI, "approve", [vault.address, ethers.MaxUint256]);
      setStatus({ text: "Approved!", ok: true }); onRefresh();
    } catch (e: unknown) {
      setStatus({ text: e instanceof Error ? e.message : "Approval failed" });
    } finally { setBusy(false); }
  }

  async function handleDeposit() {
    if (!signer || !amount) return;
    setBusy(true); setStatus({ text: "Submitting deposit..." });
    try {
      await sendAndWait(vault.address, VAULT_ABI, "deposit", [ethers.parseEther(amount)]);
      setStatus({ text: "Deposited! 🎉", ok: true }); setAmount(""); onRefresh();
    } catch (e: unknown) {
      setStatus({ text: e instanceof Error ? e.message : "Deposit failed" });
    } finally { setBusy(false); }
  }

  async function handleWithdraw() {
    if (!signer || !amount) return;
    setBusy(true); setStatus({ text: `Submitting ${emergency ? "emergency " : ""}withdrawal...` });
    try {
      const method = emergency ? "emergencyWithdraw" : "withdraw";
      await sendAndWait(vault.address, VAULT_ABI, method, [ethers.parseEther(amount)]);
      setStatus({ text: "Withdrawn! 🎉", ok: true }); setAmount(""); onRefresh();
    } catch (e: unknown) {
      setStatus({ text: e instanceof Error ? e.message : "Withdrawal failed" });
    } finally { setBusy(false); }
  }

  const needsApproval =
    tab === "deposit" &&
    parseFloat(amount || "0") > 0 &&
    parseFloat(vault.lpAllowance) < parseFloat(amount || "0");

  return (
    <div style={s.card}>
      {/* Header */}
      <div style={s.header}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={s.vaultName}>{vault.name}</span>
            <span style={{
              ...s.poolBadge,
              background: vault.isStable ? "#0d2d1f" : "#1a1428",
              color:      vault.isStable ? "#4ade80" : "#a78bfa",
              border:     `1px solid ${vault.isStable ? "#166534" : "#6d28d9"}`,
            }}>
              {vault.isStable ? "Stable" : "Volatile"}
            </span>
          </div>
          <div style={s.vaultSub}>Aerodrome • Base</div>
        </div>
        <AprBadge apr={vault.apr} aprCompounded={vault.aprCompounded} />
      </div>

      {/* Stats grid */}
      <div style={s.statsGrid}>
        <Stat label="Pool TVL"          value={fmtPoolTvl(vault.poolTvlUsd)} />
        <Stat label="Pending Rewards"  value={`${fmt(vault.pendingRewards, 4)} AERO`} highlight />
        <Stat label="Perf Fee"         value={<PerfFeeTooltip perfPct={perfPct} withdrawPct={withdrawPct} />} />
        <Stat label="Withdraw Fee"     value={`${withdrawPct}%`} />
        <Stat label="Last Harvest"     value={lastHarvest} />
        <Stat label="Total Compounded" value={`${fmt(vault.totalRewardsCompounded, 2)} AERO`} />
      </div>

      {/* User position */}
      {hasPosition && (
        <div style={s.position}>
          <span style={s.posLabel}>Your position</span>
          <span style={s.posValue}>
            {fmt(vault.userShares, 6)} shares ≈ {fmt(vault.userLpValue, 6)} LP
          </span>
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        <button
          style={{ ...s.tab, ...(tab === "deposit"  ? s.tabActive : {}) }}
          onClick={() => { setTab("deposit");  setAmount(""); setStatus(null); }}
        >Deposit</button>
        <button
          style={{ ...s.tab, ...(tab === "withdraw" ? s.tabActive : {}) }}
          onClick={() => { setTab("withdraw"); setAmount(""); setStatus(null); }}
        >Withdraw</button>
      </div>

      {/* Input */}
      <div style={s.inputRow}>
        <input
          style={s.input}
          type="number"
          placeholder="0.0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          disabled={busy}
          min="0"
          step="any"
        />
        <button
          style={s.maxBtn}
          disabled={busy}
          onClick={() => setAmount(tab === "deposit" ? vault.userLpBalance : vault.userShares)}
        >MAX</button>
      </div>

      {/* Balance hint with LP tooltip */}
      <div style={s.balanceHint}>
        {tab === "deposit" ? (
          <span>
            Wallet: {fmt(vault.userLpBalance, 6)} LP tokens
            <LpTooltip />
          </span>
        ) : (
          <span>Shares: {fmt(vault.userShares, 6)}</span>
        )}
      </div>

      {/* Get LP link — always visible on deposit tab */}
      {tab === "deposit" && (
        <GetLpButton token0={vault.token0} token1={vault.token1} isStable={vault.isStable} />
      )}

      {/* Withdraw estimate */}
      {tab === "withdraw" && estimatedLpOut && (
        <div style={s.estimate}>
          <span>≈ {estimatedLpOut.net} LP</span>
          <span style={{ color: COLORS.textMuted }}>
            ({withdrawPct}% fee = {estimatedLpOut.fee} LP)
          </span>
        </div>
      )}

      {/* Emergency toggle */}
      {tab === "withdraw" && (
        <label style={s.checkLabel}>
          <input
            type="checkbox"
            checked={emergency}
            onChange={(e) => setEmerg(e.target.checked)}
            disabled={busy}
            style={{ marginRight: 6 }}
          />
          Emergency withdraw (no fee, works when paused)
        </label>
      )}

      {/* Action button */}
      {tab === "deposit" && needsApproval ? (
        <button style={{ ...s.btn, background: COLORS.yellow, color: "#000" }}
          onClick={handleApprove} disabled={busy || !signer}>
          {busy ? "Approving..." : "Approve LP Token"}
        </button>
      ) : tab === "deposit" ? (
        <button style={s.btn}
          onClick={handleDeposit}
          disabled={busy || !signer || !amount || parseFloat(amount) <= 0}>
          {busy ? "Depositing..." : "Deposit"}
        </button>
      ) : (
        <button
          style={{ ...s.btn, background: emergency ? COLORS.red : COLORS.primary }}
          onClick={handleWithdraw}
          disabled={busy || !signer || !amount || parseFloat(amount) <= 0}>
          {busy ? "Withdrawing..." : emergency ? "Emergency Withdraw" : "Withdraw"}
        </button>
      )}

      {/* Status */}
      {status && (
        <div style={{ ...s.status, color: status.ok ? COLORS.green : COLORS.red }}>
          {status.text}
        </div>
      )}
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────
function fmtPoolTvl(usd: number | null | undefined): string {
  if (!usd) return "—";
  if (usd >= 1_000_000) return "$" + (usd / 1_000_000).toFixed(2) + "M";
  if (usd >= 1_000)     return "$" + (usd / 1_000).toFixed(1) + "K";
  return "$" + usd.toFixed(0);
}

const Stat: React.FC<{ label: string; value: React.ReactNode; highlight?: boolean }> = ({
  label, value, highlight,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.05em" }}>
      {label}
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: highlight ? COLORS.green : COLORS.textPrimary }}>
      {value}
    </div>
  </div>
);

function fmt(val: string, decimals = 4): string {
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return "0";
  if (n > 0 && n < Math.pow(10, -decimals)) {
    const magnitude = Math.floor(Math.log10(n));
    return n.toFixed(Math.min(Math.abs(magnitude) + 2, 12));
  }
  return n.toFixed(decimals);
}

function timeSince(date: Date): string {
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const s: Record<string, React.CSSProperties> = {
  card: {
    background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    padding: 24,
    marginBottom: 16,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
    gap: 12,
  },
  vaultName: { fontSize: 18, fontWeight: 800, color: COLORS.textPrimary },
  poolBadge: {
    display: "inline-block",
    fontSize: 10, fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    padding: "2px 7px", borderRadius: 4,
    verticalAlign: "middle",
  },
  vaultSub: { fontSize: 12, color: COLORS.textMuted, marginTop: 2 },
  aprBadge: {
    background: "#1e1e3f",
    borderRadius: 8,
    padding: "6px 12px",
    textAlign: "right" as const,
    flexShrink: 0,
  },
  aprLabel: {
    display: "block", fontSize: 10,
    color: COLORS.textMuted,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
  aprValue: { display: "block", fontSize: 16, fontWeight: 700 },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px 16px",
    marginBottom: 16,
  },
  position: {
    background: "#1a1a35",
    borderRadius: 8,
    padding: "8px 12px",
    marginBottom: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  posLabel: { fontSize: 12, color: COLORS.textMuted },
  posValue: { fontSize: 13, fontWeight: 600, color: COLORS.accent },
  tabs: {
    display: "flex", gap: 4, marginBottom: 12,
    background: "#0d0d20", borderRadius: 8, padding: 4,
  },
  tab: {
    flex: 1, padding: "8px 0", borderRadius: 6,
    border: "none", background: "transparent",
    color: COLORS.textMuted, fontSize: 14, fontWeight: 600, cursor: "pointer",
  },
  tabActive: { background: COLORS.bgCard, color: COLORS.textPrimary },
  inputRow: { display: "flex", gap: 8, marginBottom: 4 },
  input: {
    flex: 1, padding: "10px 12px", borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    background: COLORS.bgInput, color: COLORS.textPrimary,
    fontSize: 15, outline: "none",
  },
  maxBtn: {
    padding: "10px 14px", borderRadius: 8, border: "none",
    background: "#1e1e3f", color: COLORS.textSub,
    cursor: "pointer", fontSize: 12, fontWeight: 600,
  },
  balanceHint: { fontSize: 12, color: COLORS.textMuted, marginBottom: 8 },
  getLpBtn: {
    display: "block",
    width: "100%",
    padding: "9px 0",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    background: "transparent",
    color: COLORS.accent,
    fontSize: 13,
    fontWeight: 600,
    textAlign: "center" as const,
    textDecoration: "none",
    marginBottom: 10,
    cursor: "pointer",
    transition: "background 0.15s",
  },
  estimate: {
    fontSize: 12, color: COLORS.textSub, marginBottom: 8,
    display: "flex", gap: 8, flexWrap: "wrap" as const,
  },
  checkLabel: {
    display: "flex", alignItems: "center",
    fontSize: 12, color: COLORS.textMuted,
    marginBottom: 12, cursor: "pointer",
    userSelect: "none" as const,
  },
  btn: {
    width: "100%", padding: "13px 0", borderRadius: 8,
    border: "none", background: COLORS.primary,
    color: "#fff", fontSize: 15, fontWeight: 700,
    cursor: "pointer", marginTop: 4,
  },
  status: { marginTop: 10, fontSize: 13 },
};